/**
 * The in-world faction vendor — #11.
 *
 * Deliberately *not* a shop screen. docs/13 §5 records that the altar's shape —
 * one thing, standing in the world, taken by walking to it and pressing E — is
 * the most Noita-unlike and best-received interaction in the build, and docs/11
 * argues that reusing a strong interaction beats adding a second weaker one. So
 * this is the altar with a price list: no scrim, no pause, no cursor capture,
 * and the game keeps running behind it.
 *
 * Two states, and the difference is the whole design:
 *
 *   out of range — a name and a standing. Nothing else. Reading what is for sale
 *                  costs you a walk, which is what makes the vendor a *place*.
 *   in range     — the stock, priced, against the scrip you are actually holding.
 *
 * The panel reports nothing and decides nothing: it renders `VendorVM` and the
 * integrator owns the cursor, the purchase and every refusal sentence. It is
 * pointer-inert for the same reason the HUD is — a click here must fall through
 * to the canvas, because the player is still fighting.
 */

import { clear, el, TextBind } from './style';
import { standingTone, type VendorVM } from './viewModels';

export class VendorPanel {
  readonly root: HTMLElement;

  private head: HTMLElement;
  private nameBind: TextBind;
  private standing: HTMLElement;
  private purse: TextBind;
  private body: HTMLElement;

  /**
   * What the body was last built from. The vendor sits on the per-frame path
   * for as long as the player stands at it, and its contents change only when
   * the purse, the cursor or the stock does — so the rows are rebuilt against
   * this key rather than every frame.
   */
  private lastKey = '\u0000';

  constructor() {
    this.root = el('div', 'vend');
    this.root.hidden = true;

    this.head = el('div', 'vend__head');
    const name = el('span', 'vend__name');
    this.standing = el('span', 'fx__state');
    const purse = el('span', 'vend__purse');
    this.head.append(name, this.standing, purse);
    this.nameBind = new TextBind(name);
    this.purse = new TextBind(purse);

    this.body = el('div', 'vend__body');
    this.root.append(this.head, this.body);
  }

  update(v: VendorVM | null): void {
    if (!v) {
      if (!this.root.hidden) {
        this.root.hidden = true;
        // Reset so walking back to the same vendor rebuilds rather than showing
        // a stale body that happens to match the key.
        this.lastKey = '\u0000';
      }
      return;
    }
    if (this.root.hidden) this.root.hidden = false;

    this.nameBind.set(v.factionName);
    const tone = standingTone(v.standing);
    if (this.standing.dataset.tone !== tone) this.standing.dataset.tone = tone;
    if (this.standing.textContent !== v.standing) this.standing.textContent = v.standing;

    if (this.root.dataset.range !== (v.inRange ? '1' : '0'))
      this.root.dataset.range = v.inRange ? '1' : '0';
    if (this.root.dataset.open !== (v.open ? '1' : '0'))
      this.root.dataset.open = v.open ? '1' : '0';
    if (this.root.dataset.hostile !== (v.hostile ? '1' : '0'))
      this.root.dataset.hostile = v.hostile ? '1' : '0';

    // The purse only belongs on screen when there is something to weigh it
    // against; out of range it is noise on a signpost.
    this.purse.set(v.inRange ? v.scrip + ' scrip' : '');

    const key = vendorKey(v);
    if (key === this.lastKey) return;
    this.lastKey = key;
    clear(this.body);

    if (!v.inRange) return;

    if (!v.open) {
      // A shut door names its own cause. "They will not trade" with no reason
      // is indistinguishable from a bug, and the reason is always recoverable —
      // it is the opposite pole's standing.
      this.body.append(el('div', 'vend__refusal', v.refusal || 'they will not trade with you'));
      return;
    }

    if (v.offers.length === 0) {
      this.body.append(el('div', 'vend__why', v.refusal || 'nothing left on the cloth'));
      return;
    }

    // The whole shelf, including the lines standing has not opened. A shelf
    // that silently grows as reputation improves teaches nothing; two greyed
    // rows reading "needs their favour" is the standing system explaining
    // itself at the point of use.
    for (const o of v.offers) {
      const row = el('div', 'vend__row');
      row.dataset.can = o.refusal === '' ? '1' : '0';
      row.dataset.on = o.id === v.counterId ? '1' : '0';
      row.append(el('span', 'vend__key', o.id === v.counterId ? '▸' : ''));
      row.append(el('span', 'vend__label', o.label));
      row.append(el('span', 'vend__price', o.price + '¤'));
      this.body.append(row);
      if (o.refusal) this.body.append(el('div', 'vend__why', '   ' + o.refusal));
    }

    const counter = v.offers.find((o) => o.id === v.counterId);
    const takeable = counter !== undefined && counter.refusal === '';
    const foot = el('div', takeable ? 'vend__take' : 'vend__why');
    foot.append(
      document.createTextNode(
        takeable ? '▸ E to take' : counter ? counter.refusal : 'nothing on the counter',
      ),
    );
    // The clock is what makes standing here a decision rather than a menu: the
    // counter turns over whether or not you have made up your mind.
    if (v.rotateIn > 0) {
      foot.append(el('span', 'vend__clock', ' · turns over in ' + Math.ceil(v.rotateIn) + 's'));
    }
    this.body.append(foot);

    if (v.hostile) {
      // Hostility with a consequence past a closed door (#11): the stall is
      // still there, and so are they.
      this.body.append(el('div', 'vend__refusal', 'they are waiting for you here'));
    }
  }
}

/**
 * Everything the body is drawn from, in one string.
 *
 * Affordability is folded in through each line's refusal, so a purse that ticks
 * past a price re-renders the row that just became takeable — which is the one
 * change a naive "same stock, same cursor" check would miss.
 */
function vendorKey(v: VendorVM): string {
  let k =
    v.factionId + '|' + (v.inRange ? '1' : '0') + '|' + (v.open ? '1' : '0') +
    '|' + (v.hostile ? '1' : '0') + '|' + v.counterId + '|' + v.refusal +
    // Whole seconds only. The clock ticks every frame and the readout is
    // `Math.ceil`d, so keying on the raw float would rebuild the shelf 60 times
    // a second to draw the same number.
    '|' + Math.ceil(v.rotateIn);
  for (const o of v.offers) k += '|' + o.id + ':' + o.price + ':' + o.refusal;
  return k;
}
